import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './BullsEye.css';

// Contract ABIs
const BULLSEYE_ABI = [
  "function shoot(uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 result, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 outerRingChance, uint256 middleRingChance, uint256 innerRingChance, uint256 bullseyeChance, uint256 outerRingPayout, uint256 middleRingPayout, uint256 innerRingPayout, uint256 bullseyePayout))",
  "function getTargetZoneInfo(uint8 zone) external view returns (uint256 chance, uint256 payout, string name)",
  "function calculateHouseEdge() external view returns (uint256)",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateTargetChances(uint256 noWin, uint256 outer, uint256 middle, uint256 inner, uint256 bullseye) external",
  "function updateTargetPayouts(uint256 outerPayout, uint256 middlePayout, uint256 innerPayout, uint256 bullseyePayout) external",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 result, uint256 grossPayout, uint256 netPayout, bool won)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  BULLSEYE: "0x683984F1d7E5485e45C4112E8E234CC24208Aff2", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// Tournament zones configuration
const TOURNAMENT_ZONES = [
  { id: 0, name: 'MISS', icon: '💨', color: '#8B4513', description: 'Arrow missed target', payout: '0x', sound: 'miss' },
  { id: 1, name: 'OUTER RING', icon: '🎯', color: '#CD853F', description: 'Outer ring hit', payout: '1.5x', sound: 'thud' },
  { id: 2, name: 'MIDDLE RING', icon: '🏹', color: '#DAA520', description: 'Middle ring accuracy', payout: '3x', sound: 'hit' },
  { id: 3, name: 'INNER RING', icon: '⭐', color: '#FF8C00', description: 'Inner ring precision', payout: '8x', sound: 'strike' },
  { id: 4, name: 'BULLSEYE', icon: '🎯', color: '#DC143C', description: 'Perfect bullseye!', payout: '20x', sound: 'bullseye' }
];

// 🎵 MEDIEVAL TAVERN SOUND SYSTEM 🎵
const playTavernSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'bowCreak':
        createComplexSound([
          { freq: 150, duration: 0.8, wave: 'sawtooth', delay: 0 },
          { freq: 180, duration: 0.8, wave: 'triangle', delay: 200 },
          { freq: 200, duration: 0.6, wave: 'sine', delay: 400 }
        ], 0.6);
        break;
        
      case 'arrowWhistle':
        createComplexSound([
          { freq: 800, duration: 0.6, wave: 'triangle', delay: 0 },
          { freq: 600, duration: 0.8, wave: 'sine', delay: 100 },
          { freq: 400, duration: 1.0, wave: 'triangle', delay: 200 }
        ], 0.7);
        break;
        
      case 'targetThud':
        createComplexSound([
          { freq: 120, duration: 0.3, wave: 'square', delay: 0 },
          { freq: 80, duration: 0.4, wave: 'sawtooth', delay: 50 },
          { freq: 60, duration: 0.5, wave: 'sine', delay: 100 }
        ], 0.3);
        break;
        
      case 'bullseyeCheer':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 200 },
          { freq: 784, duration: 0.5, wave: 'sine', delay: 400 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 600 },
          { freq: 1319, duration: 0.7, wave: 'sine', delay: 800 }
        ], 0.5);
        break;
        
      case 'tavernCheer':
        createComplexSound([
          { freq: 440, duration: 0.5, wave: 'triangle', delay: 0 },
          { freq: 330, duration: 0.5, wave: 'sine', delay: 100 },
          { freq: 220, duration: 0.6, wave: 'triangle', delay: 200 }
        ], 0.4);
        break;
        
      case 'coinDrop':
        createComplexSound([
          { freq: 1200, duration: 0.1, wave: 'triangle', delay: 0 },
          { freq: 800, duration: 0.15, wave: 'sine', delay: 50 },
          { freq: 600, duration: 0.2, wave: 'triangle', delay: 100 }
        ], 0.15);
        break;
        
      case 'woodClick':
        createComplexSound([
          { freq: 400, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 300, duration: 0.08, wave: 'triangle', delay: 20 }
        ], 0.06);
        break;
        
      case 'tavernHum':
        createTone(220, 0.15, 'sine');
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const BullsEye = ({ walletData, onBack }) => {
  const [tournamentState, setTournamentState] = useState({
    wagerAmount: '10',
    isAiming: false,
    isApproving: false,
    lastShot: null,
    balance: '0',
    allowance: '0',
    archerRank: 1,
    tournamentPoints: 0,
    totalShots: 0,
    bullseyeHits: 0,
    showShootingModal: false,
    bowReadiness: 0
  });

  const [tournamentStats, setTournamentStats] = useState({
    totalRounds: 0,
    totalWagers: '0',
    totalWinnings: '0',
    houseAdvantage: '15',
    bestMultiplier: '1.50',
    activeArchers: 0
  });

  const [shotHistory, setShotHistory] = useState([]);
  const [shootingPhase, setShootingPhase] = useState('idle');
  const [shotOutcome, setShotOutcome] = useState(null);
  const [tavernAtmosphere, setTavernAtmosphere] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    bullsEye: null,
    blbToken: null
  });
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
  houseBalance: '0',
  isPaused: false,
  fundAmount: '',
  withdrawAmount: '',
  minBet: '1',
  maxBet: '1000000',
  newMinBet: '',
  newMaxBet: '',
  newTargetChances: {
    miss: '50.0',
    outer: '35.0',
    middle: '10.0',
    inner: '4.0',
    bullseye: '1.0'
  },
  newTargetPayouts: {
    outer: '150',
    middle: '300',
    inner: '800',
    bullseye: '2000'
  }
});

  const targetRef = useRef(null);
  const bowRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playTavernSound(type, volume);
    }
  };

  // Tavern atmosphere effects
  useEffect(() => {
    const generateTavernAtmosphere = () => {
      const effects = [];
      const effectCount = 25 + (tournamentState.archerRank * 3);
      
      const effectTypes = ['🕯️', '💰', '🍺', '⚔️', '🏰', '📜'];
      
      for (let i = 0; i < effectCount; i++) {
        effects.push({
          id: i,
          type: effectTypes[Math.floor(Math.random() * effectTypes.length)],
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 20 + 15,
          speed: Math.random() * 0.8 + 0.2,
          opacity: Math.random() * 0.4 + 0.1,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 2
        });
      }
      setTavernAtmosphere(effects);
    };

    generateTavernAtmosphere();
    
    const interval = setInterval(() => {
      setTavernAtmosphere(prev => prev.map(effect => ({
        ...effect,
        y: (effect.y + effect.speed * 0.1) % 105,
        rotation: (effect.rotation + effect.rotationSpeed) % 360,
        opacity: 0.1 + Math.sin(Date.now() * 0.002 + effect.id) * 0.3
      })));
    }, 150);

    return () => clearInterval(interval);
  }, [tournamentState.archerRank]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ bullsEye: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const bullsEyeContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BULLSEYE,
        BULLSEYE_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        bullsEye: bullsEyeContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadTournamentData(bullsEyeContract);
        loadBalance(blbTokenContract, bullsEyeContract);
        checkAdminAccess(bullsEyeContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ bullsEye: null, blbToken: null });
    }
  };

  // Check admin access
  const checkAdminAccess = async (contract = contracts.bullsEye) => {
    if (!contract || !walletData?.account) return;

    try {
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.bullsEye) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Load tournament data
  const loadTournamentData = async (contract = contracts.bullsEye) => {
    if (!contract) return;

    try {
      const [stats, recentShots, config] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.getGameConfig()
      ]);

      setTournamentStats({
        totalRounds: parseInt(stats.totalGames.toString()),
        totalWagers: ethers.utils.formatEther(stats.totalBets),
        totalWinnings: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseAdvantage: '15',
        bestMultiplier: (parseInt(config.outerRingPayout.toString()) / 100).toString(),
        activeArchers: Math.floor(Math.random() * 80) + 15
      });

      if (recentShots && recentShots.length > 0) {
        const formattedHistory = recentShots.slice(0, 5).map(shot => ({
          result: parseInt(shot.result),
          won: shot.won,
          wagerAmount: ethers.utils.formatEther(shot.betAmount),
          winnings: ethers.utils.formatEther(shot.netPayout),
          timestamp: parseInt(shot.timestamp) * 1000
        }));
        setShotHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading tournament data:', error);
    }
  };

  // Load balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.bullsEye) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setTournamentState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // Handle shooting
  const handleTakeShot = async () => {
    if (!contracts.bullsEye || !contracts.blbToken) {
      setErrorMessage('Tournament connection lost. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const wagerAmount = parseFloat(tournamentState.wagerAmount);
      const balance = parseFloat(tournamentState.balance);

      if (wagerAmount <= 0) {
        setErrorMessage('Wager amount must be greater than 0');
        return;
      }

      if (wagerAmount > balance) {
        setErrorMessage('Insufficient tournament coins');
        return;
      }

      // Start aiming sequence
      playGameSound('bowCreak', 0.4);
      
      setTournamentState(prev => ({ 
        ...prev, 
        isAiming: true,
        showShootingModal: true,
        bowReadiness: 0
      }));
      setShootingPhase('aiming');

      // Bow readiness buildup
      const readinessInterval = setInterval(() => {
        setTournamentState(prev => ({
          ...prev,
          bowReadiness: Math.min(prev.bowReadiness + 8, 100)
        }));
      }, 80);

      setTimeout(() => {
        clearInterval(readinessInterval);
        executeShot();
      }, 2500);

    } catch (error) {
      console.error('Shooting preparation error:', error);
      setShootingPhase('idle');
      setTournamentState(prev => ({ 
        ...prev, 
        isAiming: false,
        showShootingModal: false,
        bowReadiness: 0
      }));
    }
  };

  const executeShot = async () => {
    try {
      playGameSound('arrowWhistle', 0.5);
      setShootingPhase('shooting');
      setTournamentState(prev => ({ ...prev, bowReadiness: 0 }));

      const wagerAmountWei = ethers.utils.parseEther(tournamentState.wagerAmount);
      const tx = await contracts.bullsEye.shoot(wagerAmountWei);
      
      setSuccessMessage('Arrow loosed! Flying to target...');
      
      const receipt = await tx.wait();
      
      const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
      
      if (gamePlayedEvent) {
        const [player, wagerAmount, result, grossPayout, netPayout, won] = gamePlayedEvent.args;
        
        const shotResult = {
          player,
          wagerAmount: ethers.utils.formatEther(wagerAmount),
          result: parseInt(result),
          grossPayout: ethers.utils.formatEther(grossPayout),
          netPayout: ethers.utils.formatEther(netPayout),
          won,
          timestamp: Date.now()
        };

        setTimeout(() => {
          handleShotResult(shotResult);
        }, 1800);
        
        await Promise.all([loadTournamentData(), loadBalance()]);
      } else {
        throw new Error('Tournament event not found');
      }

    } catch (error) {
      console.error('Shot execution error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Shot cancelled by archer');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient coins or gas');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please approve coin spending first');
      } else {
        setErrorMessage(`Shot failed: ${error.reason || error.message}`);
      }
      
      setShootingPhase('idle');
      setTournamentState(prev => ({ 
        ...prev, 
        isAiming: false,
        showShootingModal: false
      }));
      setSuccessMessage('');
    }
  };

  const handleShotResult = (shotResult) => {
    setShotOutcome(shotResult);
    setShootingPhase('result');

    setTimeout(() => {
      if (shotResult.won) {
        if (shotResult.result === 4) { // Bullseye
          playGameSound('bullseyeCheer', 0.8);
          setSuccessMessage(`🎯 PERFECT BULLSEYE! The tavern erupts! +${formatNumber(shotResult.netPayout)} BLB`);
          setTournamentState(prev => ({
            ...prev,
            bullseyeHits: prev.bullseyeHits + 1,
            tournamentPoints: prev.tournamentPoints + 1000
          }));
        } else {
          playGameSound('targetThud', 0.6);
          const zoneName = TOURNAMENT_ZONES[shotResult.result]?.name || 'TARGET HIT';
          setSuccessMessage(`🏹 ${zoneName}! Well aimed! +${formatNumber(shotResult.netPayout)} BLB`);
          setTournamentState(prev => ({
            ...prev,
            tournamentPoints: prev.tournamentPoints + (100 * (shotResult.result + 1))
          }));
        }
      } else {
        playGameSound('tavernCheer', 0.3);
        setErrorMessage(`💨 Arrow wide of the mark! The crowd groans! -${formatNumber(shotResult.wagerAmount)} BLB`);
        setTournamentState(prev => ({
          ...prev,
          tournamentPoints: prev.tournamentPoints + 25
        }));
      }
    }, 500);

    setTournamentState(prev => ({
      ...prev,
      isAiming: false,
      totalShots: prev.totalShots + 1,
      archerRank: Math.floor((prev.tournamentPoints + (shotResult.won ? 200 : 25)) / 2000) + 1
    }));

    setTimeout(() => {
      setShootingPhase('idle');
      setShotOutcome(null);
      setTournamentState(prev => ({ 
        ...prev, 
        isAiming: false,
        showShootingModal: false
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4500);
  };

  // Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.bullsEye) {
      setErrorMessage('Tournament connection lost. Please reconnect.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('woodClick', 0.3);
      setTournamentState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.bullsEye.address, maxAmount);
      
      setSuccessMessage('Tournament entry being processed...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ Tournament entry approved! Ready to compete!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Tournament entry rejected');
      } else {
        setErrorMessage(`Entry failed: ${error.reason || error.message}`);
      }
    } finally {
      setTournamentState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const needsApproval = () => {
    return parseFloat(tournamentState.allowance) < parseFloat(tournamentState.wagerAmount);
  };

  const canTakeShot = () => {
    return connected && 
           contracts.bullsEye &&
           contracts.blbToken &&
           parseFloat(tournamentState.balance) >= parseFloat(tournamentState.wagerAmount) &&
           parseFloat(tournamentState.wagerAmount) > 0 &&
           !needsApproval() &&
           !tournamentState.isAiming;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getArcherTitle = (rank) => {
    if (rank >= 50) return 'Legendary Marksman';
    if (rank >= 40) return 'Master Archer';
    if (rank >= 30) return 'Expert Bowman';
    if (rank >= 20) return 'Skilled Archer';
    if (rank >= 10) return 'Apprentice Marksman';
    return 'Novice Archer';
  };

  const getZoneName = (result) => {
    return TOURNAMENT_ZONES[result]?.name || 'UNKNOWN';
  };

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  if (!connected) {
    return (
      <div className="medieval-tournament-hall">
        <div className="tavern-atmosphere">
          {tavernAtmosphere.map(effect => (
            <div
              key={effect.id}
              className="atmosphere-effect"
              style={{
                left: `${effect.x}%`,
                top: `${effect.y}%`,
                opacity: effect.opacity,
                fontSize: `${effect.size}px`,
                transform: `rotate(${effect.rotation}deg)`,
                animationDelay: `${effect.id * 0.2}s`
              }}
            >
              {effect.type}
            </div>
          ))}
        </div>
        <div className="tavern-entrance-screen">
          <div className="entrance-icon">🏰</div>
          <h3 className="entrance-title">🍺 TAVERN CLOSED</h3>
          <p className="entrance-desc">Present your credentials to enter the Medieval Archery Tournament</p>
        </div>
      </div>
    );
  }

  if (!contracts.bullsEye || !contracts.blbToken) {
    return (
      <div className="medieval-tournament-hall">
        <div className="tavern-atmosphere">
          {tavernAtmosphere.map(effect => (
            <div
              key={effect.id}
              className="atmosphere-effect"
              style={{
                left: `${effect.x}%`,
                top: `${effect.y}%`,
                opacity: effect.opacity,
                fontSize: `${effect.size}px`,
                transform: `rotate(${effect.rotation}deg)`,
                animationDelay: `${effect.id * 0.2}s`
              }}
            >
              {effect.type}
            </div>
          ))}
        </div>
        <div className="tavern-entrance-screen">
          <div className="entrance-icon">⚔️</div>
          <h3 className="entrance-title">🔨 PREPARING TOURNAMENT</h3>
          <p className="entrance-desc">The tournament masters are setting up the archery range...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="medieval-tournament-hall">
      {/* Tavern Atmosphere Background */}
      <div className="tavern-atmosphere">
        {tavernAtmosphere.map(effect => (
          <div
            key={effect.id}
            className="atmosphere-effect"
            style={{
              left: `${effect.x}%`,
              top: `${effect.y}%`,
              opacity: effect.opacity,
              fontSize: `${effect.size}px`,
              transform: `rotate(${effect.rotation}deg)`,
              animationDelay: `${effect.id * 0.2}s`
            }}
          >
            {effect.type}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="tournament-header-banner">
        <button 
          className="tavern-exit-btn" 
          onClick={() => {
            playGameSound('woodClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('tavernHum', 0.2)}
        >
          ← EXIT TAVERN
        </button>
        <div className="tournament-title-section">
          <h2 className="tournament-main-title">🏰 MEDIEVAL ARCHERY TOURNAMENT</h2>
          <div className="tournament-subtitle">Ye Olde Tavern Championship</div>
        </div>
        <div className="coin-purse-display">
          💰 {formatNumber(tournamentState.balance)} BLB
          <div className="archer-rank-display">
            {getArcherTitle(tournamentState.archerRank)}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('woodClick', 0.2);
            }}
            className="sound-toggle-btn"
            title={soundEnabled ? 'Mute Sounds' : 'Enable Sounds'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('woodClick', 0.3);
              }}
              onMouseEnter={() => playGameSound('tavernHum', 0.2)}
              className={`tournament-master-btn ${showAdminPanel ? 'active' : ''}`}
              title="Tournament Master Panel"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="tavern-message tavern-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="tavern-message tavern-success-msg">
          {successMessage}
        </div>
      )}

      <div className="tournament-layout">

        {/* Mobile-Optimized Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="tournament-master-panel">
            <div className="master-panel-header">
              <h3>⚔️ TOURNAMENT MASTER</h3>
              <div className="master-role-badge">
                {isOwner ? '👑 GRAND MASTER' : '⚔️ MASTER'}
              </div>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="admin-close-btn"
              >
                ✕
              </button>
            </div>

            <div className="admin-panel-content">
              {/* Tournament Status */}
              <div className="admin-section">
                <h4 className="admin-section-title">🏰 Tournament Status</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'rgba(205, 133, 63, 0.9)', fontWeight: '600' }}>Status:</span>
                  <span style={{
                    color: adminState.isPaused ? '#FFB6C1' : '#90EE90',
                    fontWeight: '800',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)'
                  }}>
                    {adminState.isPaused ? '⏸️ PAUSED' : '▶️ ACTIVE'}
                  </span>
                </div>
                <button
                  className="admin-btn"
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('woodClick', 0.3);
                      
                      if (contracts.bullsEye) {
                        const tx = adminState.isPaused ? 
                          await contracts.bullsEye.unpauseGame() : 
                          await contracts.bullsEye.pauseGame();
                        
                        setSuccessMessage(`Tournament ${adminState.isPaused ? 'resumed' : 'paused'}...`);
                        await tx.wait();
                        
                        await loadAdminData();
                        setSuccessMessage(`✅ Tournament ${adminState.isPaused ? 'resumed' : 'paused'} successfully!`);
                      } else {
                        setSuccessMessage(`✅ Tournament ${adminState.isPaused ? 'resumed' : 'paused'}! (Demo)`);
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error toggling game state:', error);
                      setErrorMessage(`Failed to ${adminState.isPaused ? 'resume' : 'pause'} tournament: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: adminState.isPaused ? 'linear-gradient(135deg, #228B22 0%, #006400 100%)' : 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
                    border: `3px solid ${adminState.isPaused ? '#32CD32' : '#DC143C'}`,
                    borderRadius: '10px',
                    color: adminState.isPaused ? '#90EE90' : '#FFB6C1',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {adminState.isPaused ? '▶️ RESUME' : '⏸️ PAUSE'}
                </button>
              </div>

              {/* House Treasury */}
              <div className="admin-section">
                <h4 className="admin-section-title">💰 House Treasury</h4>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(205, 133, 63, 0.9)', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Current Balance:
                  </div>
                  <div style={{ 
                    color: '#DAA520', 
                    fontSize: '1.5rem', 
                    fontWeight: '800',
                    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)'
                  }}>
                    ✨ {formatNumber(adminState.houseBalance)} BLB
                  </div>
                </div>
                <button
                  className="admin-btn"
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('woodClick', 0.3);
                      
                      if (contracts.bullsEye && contracts.bullsEye.emergencyWithdrawAll) {
                        const tx = await contracts.bullsEye.emergencyWithdrawAll();
                        setSuccessMessage('Extracting all treasury funds...');
                        await tx.wait();
                        
                        await Promise.all([loadAdminData(), loadBalance()]);
                        setSuccessMessage('✅ Emergency extraction completed!');
                      } else {
                        setSuccessMessage('✅ Emergency extraction completed! (Demo)');
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Emergency withdraw error:', error);
                      setErrorMessage(`Emergency extraction failed: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
                    border: '3px solid #DC143C',
                    borderRadius: '10px',
                    color: '#FFB6C1',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase'
                  }}
                >
                  🚨 EMERGENCY EXTRACTION
                </button>
              </div>

              {/* Fund Treasury */}
              <div className="admin-section">
                <h4 className="admin-section-title">💳 Fund Treasury</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    placeholder="Amount to fund treasury"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                      border: '3px solid #8B4513',
                      borderRadius: '10px',
                      color: '#DAA520',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    className="admin-btn"
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.fundAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid fund amount');
                          return;
                        }
                        
                        playGameSound('coinDrop', 0.3);
                        
                        if (contracts.bullsEye && contracts.blbToken) {
                          const amountWei = ethers.utils.parseEther(amount);
                          
                          const allowance = await contracts.blbToken.allowance(walletData.account, contracts.bullsEye.address);
                          if (allowance.lt(amountWei)) {
                            const approveTx = await contracts.blbToken.approve(contracts.bullsEye.address, ethers.constants.MaxUint256);
                            await approveTx.wait();
                          }
                          
                          const tx = await contracts.bullsEye.fundHouse(amountWei);
                          setSuccessMessage('Funding treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB!`);
                        } else {
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, fundAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error funding treasury:', error);
                        setErrorMessage(`Failed to fund treasury: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.fundAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #228B22 0%, #006400 100%)',
                      border: '3px solid #32CD32',
                      borderRadius: '10px',
                      color: '#90EE90',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.fundAmount ? 1 : 0.6
                    }}
                  >
                    💎 FUND TREASURY
                  </button>
                </div>
              </div>

              {/* Withdraw Treasury */}
              <div className="admin-section">
                <h4 className="admin-section-title">💸 Withdraw Treasury</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.withdrawAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    placeholder="Amount to withdraw"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                      border: '3px solid #8B4513',
                      borderRadius: '10px',
                      color: '#DAA520',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    className="admin-btn"
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.withdrawAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid withdraw amount');
                          return;
                        }
                        
                        playGameSound('coinDrop', 0.3);
                        
                        if (contracts.bullsEye) {
                          const amountWei = ethers.utils.parseEther(amount);
                          const tx = await contracts.bullsEye.withdrawHouse(amountWei);
                          setSuccessMessage('Withdrawing from treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury!`);
                        } else {
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error withdrawing from treasury:', error);
                        setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                      border: '3px solid #CD853F',
                      borderRadius: '10px',
                      color: '#2F1B14',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.withdrawAmount ? 1 : 0.6
                    }}
                  >
                    💸 WITHDRAW
                  </button>
                </div>
              </div>

              {/* Tournament Limits */}
              <div className="admin-section">
                <h4 className="admin-section-title">🎯 Tournament Limits</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIN BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMinBet || adminState.minBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMinBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MAX BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMaxBet || adminState.maxBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMaxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <button
                    className="admin-btn"
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const minBet = parseFloat(adminState.newMinBet || adminState.minBet);
                        const maxBet = parseFloat(adminState.newMaxBet || adminState.maxBet);

                        if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
                          setErrorMessage('Invalid bet limits');
                          return;
                        }

                        playGameSound('woodClick', 0.3);
                        
                        if (contracts.bullsEye) {
                          const minBetWei = ethers.utils.parseEther(minBet.toString());
                          const maxBetWei = ethers.utils.parseEther(maxBet.toString());
                          
                          const tx = await contracts.bullsEye.setBetLimits(minBetWei, maxBetWei);
                          setSuccessMessage('Updating tournament limits...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB`);
                        } else {
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error setting bet limits:', error);
                        setErrorMessage(`Failed to set limits: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8B4513 0%, #654321 100%)',
                      border: '3px solid #A0522D',
                      borderRadius: '10px',
                      color: '#DAA520',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase'
                    }}
                  >
                    🎯 SET LIMITS
                  </button>
                </div>
              </div>

              {/* Target Configuration */}
              <div className="admin-section">
                <h4 className="admin-section-title">🎯 Target Configuration</h4>
                <div style={{ fontSize: '0.85rem', color: 'rgba(205, 133, 63, 0.8)', marginBottom: '1rem', fontStyle: 'italic', textAlign: 'center' }}>
                  Configure target zone chances and payouts
                </div>
                
                {/* Chance Configuration */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ color: '#DAA520', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    TARGET CHANCES (%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MISS CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newTargetChances?.miss || '50.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newTargetChances: { ...prev.newTargetChances, miss: e.target.value }
                        }))}
                        min="0"
                        max="90"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        OUTER RING (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newTargetChances?.outer || '35.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newTargetChances: { ...prev.newTargetChances, outer: e.target.value }
                        }))}
                        min="0"
                        max="50"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIDDLE RING (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newTargetChances?.middle || '10.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newTargetChances: { ...prev.newTargetChances, middle: e.target.value }
                        }))}
                        min="0"
                        max="25"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        INNER RING (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newTargetChances?.inner || '4.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newTargetChances: { ...prev.newTargetChances, inner: e.target.value }
                        }))}
                        min="0"
                        max="10"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(205, 133, 63, 0.9)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        BULLSEYE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newTargetChances?.bullseye || '1.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newTargetChances: { ...prev.newTargetChances, bullseye: e.target.value }
                        }))}
                        min="0"
                        max="5"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'linear-gradient(135deg, rgba(47, 27, 20, 0.8) 0%, rgba(101, 67, 33, 0.6) 100%)',
                          border: '2px solid #8B4513',
                          borderRadius: '8px',
                          color: '#DAA520',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Status Info */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(139, 69, 19, 0.2)', borderRadius: '10px', border: '2px solid rgba(218, 165, 32, 0.3)' }}>
                  <div style={{ color: '#DAA520', fontWeight: '600', marginBottom: '0.5rem', textAlign: 'center' }}>
                    <strong>Total:</strong> {(
                      parseFloat(adminState.newTargetChances?.miss || 50) + 
                      parseFloat(adminState.newTargetChances?.outer || 35) +
                      parseFloat(adminState.newTargetChances?.middle || 10) +
                      parseFloat(adminState.newTargetChances?.inner || 4) +
                      parseFloat(adminState.newTargetChances?.bullseye || 1)
                    ).toFixed(1)}%
                  </div>
                  <div style={{ color: '#DAA520', fontWeight: '600', textAlign: 'center' }}>
                    <strong>House Edge:</strong> <span style={{ color: parseFloat(adminState.newTargetChances?.miss || 50) > 60 ? '#DC143C' : '#90EE90' }}>
                      {parseFloat(adminState.newTargetChances?.miss || 50).toFixed(1)}%
                    </span>
                  </div>
                  {Math.abs((
                    parseFloat(adminState.newTargetChances?.miss || 50) + 
                    parseFloat(adminState.newTargetChances?.outer || 35) +
                    parseFloat(adminState.newTargetChances?.middle || 10) +
                    parseFloat(adminState.newTargetChances?.inner || 4) +
                    parseFloat(adminState.newTargetChances?.bullseye || 1)
                  ) - 100) > 0.1 && (
                    <div style={{ color: '#DC143C', fontWeight: '800', marginTop: '0.5rem', textAlign: 'center' }}>
                      ⚠️ Must equal 100%
                    </div>
                  )}
                </div>

                {/* Update Chances Button */}
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      const totalChance = 
                        parseFloat(adminState.newTargetChances?.miss || 50) + 
                        parseFloat(adminState.newTargetChances?.outer || 35) +
                        parseFloat(adminState.newTargetChances?.middle || 10) +
                        parseFloat(adminState.newTargetChances?.inner || 4) +
                        parseFloat(adminState.newTargetChances?.bullseye || 1);
                      
                      if (Math.abs(totalChance - 100) > 0.1) {
                        setErrorMessage('Total chances must equal 100%');
                        return;
                      }

                      playGameSound('woodClick', 0.3);
                      setSuccessMessage('✅ Target chances updated successfully! (Demo)');
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error updating target chances:', error);
                      setErrorMessage(`Failed to update chances: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #8B4513 0%, #654321 100%)',
                    border: '3px solid #A0522D',
                    borderRadius: '10px',
                    color: '#DAA520',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    marginBottom: '1rem'
                  }}
                >
                  🎯 UPDATE CHANCES
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Tournament Area */}
        <div className="tournament-arena">
          {/* Shooting Modal */}
          {tournamentState.showShootingModal && (
            <div className="shooting-modal-overlay">
              <div className="shooting-modal">
                <div className="shooting-header">
                  <h2>🏹 TAKING AIM AT THE TARGET!</h2>
                  <div className="shot-info">
                    <span>Wager: {formatNumber(tournamentState.wagerAmount)} BLB</span>
                    <span>Archer: {getArcherTitle(tournamentState.archerRank)}</span>
                  </div>
                </div>

                <div className="shooting-display-area">
                  <div className="medieval-bow-display">
                    <div className="wooden-bow">
                      <div className="bow-frame">🏹</div>
                      <div 
                        className="bow-string-tension" 
                        style={{ 
                          transform: `scaleY(${1 + (tournamentState.bowReadiness / 150)})`,
                          filter: `brightness(${1 + (tournamentState.bowReadiness / 100)})`
                        }}
                      ></div>
                      <div className="iron-arrow" 
                           style={{ 
                             opacity: tournamentState.bowReadiness / 100,
                             transform: `translateX(-${tournamentState.bowReadiness / 3}px)`
                           }}>
                        ➤
                      </div>
                    </div>
                    
                    <div className="readiness-meter">
                      <div className="meter-label">BOW READINESS</div>
                      <div className="readiness-bar">
                        <div 
                          className="readiness-progress" 
                          style={{ 
                            width: `${tournamentState.bowReadiness}%`,
                            background: `linear-gradient(90deg, #8B4513 0%, #DAA520 50%, #DC143C 100%)`
                          }}
                        ></div>
                      </div>
                      <div className="readiness-percentage">{tournamentState.bowReadiness}%</div>
                    </div>
                  </div>

                  {shootingPhase === 'shooting' && (
                    <div className="arrow-flight-sequence">
                      <div className="flying-arrow">➤</div>
                      <div className="arrow-vapor-trail"></div>
                    </div>
                  )}
                </div>

                {/* Result Display in Modal */}
                {shootingPhase === 'result' && shotOutcome && (
                  <div className="shot-result-display">
                    <div className={`shot-result-content ${shotOutcome.won ? 'tournament-victory' : 'tournament-miss'}`}>
                      <div className="shot-result-icon">
                        {shotOutcome.result === 4 ? '🎯' : 
                         shotOutcome.won ? TOURNAMENT_ZONES[shotOutcome.result]?.icon : '💨'}
                      </div>
                      <h3 className="shot-result-title">
                        {shotOutcome.result === 4 ? '🎯 PERFECT BULLSEYE!' : 
                         shotOutcome.won ? `🏹 ${TOURNAMENT_ZONES[shotOutcome.result]?.name}!` : '💨 ARROW WIDE!'}
                      </h3>
                      <div className="shot-result-amount">
                        {shotOutcome.won ? `+${formatNumber(shotOutcome.netPayout)}` : `-${formatNumber(shotOutcome.wagerAmount)}`} BLB
                      </div>
                      <div className="shot-result-desc">
                        Target Zone: {getZoneName(shotOutcome.result)}
                      </div>
                      {shotOutcome.result === 4 && (
                        <div className="bullseye-celebration">
                          🎊 THE CROWD GOES WILD! PERFECT SHOT! 🎊
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Aiming indicator */}
                {shootingPhase === 'aiming' && (
                  <div className="aiming-indicator">
                    <div className="aiming-text">🏹 DRAWING BOW...</div>
                    <div className="aiming-subtext">Steady your aim, brave archer!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Target Area */}
          <div className="archery-range-area">
            <div className="tournament-range-display">
              <div className="range-header">
                <span>ARCHERY TOURNAMENT RANGE</span>
                <span className="tournament-status">READY FOR COMPETITION</span>
              </div>
              
              <div className="target-setup-area">
                <div 
                  ref={targetRef}
                  className="medieval-target-board"
                >
                  {/* Hay Bale Base */}
                  <div className="hay-bale-base">
                    <div className="hay-texture"></div>
                  </div>
                  
                  {/* Target Rings */}
                  <div className="target-ring-outer">
                    <span className="ring-multiplier">1.5x</span>
                  </div>
                  <div className="target-ring-middle">
                    <span className="ring-multiplier">3x</span>
                  </div>
                  <div className="target-ring-inner">
                    <span className="ring-multiplier">8x</span>
                  </div>
                  <div className="target-bullseye-center">
                    <span className="bullseye-multiplier">20x</span>
                    <div className="bullseye-core">🎯</div>
                  </div>
                  
                  {/* Wooden Stakes */}
                  <div className="target-stakes">
                    <div className="wooden-stake left-stake">🪵</div>
                    <div className="wooden-stake right-stake">🪵</div>
                  </div>
                </div>
                
                {/* Tournament Statistics */}
                <div className="tournament-ledger">
                  <div className="ledger-entry">
                    <span className="ledger-label">SHOTS TAKEN:</span>
                    <span className="ledger-value">{tournamentState.totalShots}</span>
                  </div>
                  <div className="ledger-entry">
                    <span className="ledger-label">BULLSEYES:</span>
                    <span className="ledger-value">{tournamentState.bullseyeHits}</span>
                  </div>
                  <div className="ledger-entry">
                    <span className="ledger-label">PRECISION:</span>
                    <span className="ledger-value">
                      {tournamentState.totalShots > 0 ? Math.round((tournamentState.bullseyeHits / tournamentState.totalShots) * 100) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tournament Controls */}
          <div className="tournament-controls">
            {/* Wager Amount */}
            <div className="tournament-control-panel">
              <label className="control-panel-label">💰 WAGER AMOUNT</label>
              <div>
                <input
                  className="wager-amount-input"
                  type="number"
                  value={tournamentState.wagerAmount}
                  onChange={(e) => setTournamentState(prev => ({ ...prev, wagerAmount: e.target.value }))}
                  disabled={tournamentState.isAiming}
                  min="1"
                  max={tournamentState.balance}
                  step="1"
                  onFocus={() => playGameSound('woodClick', 0.1)}
                />
                <div className="wager-preset-buttons">
                  {['10', '50', '200'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('coinDrop', 0.2);
                        setTournamentState(prev => ({ ...prev, wagerAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('tavernHum', 0.1)}
                      disabled={tournamentState.isAiming}
                      className="wager-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('coinDrop', 0.2);
                      setTournamentState(prev => ({ ...prev, wagerAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('tavernHum', 0.1)}
                    disabled={tournamentState.isAiming}
                    className="wager-preset-btn all-in-btn"
                  >
                    ALL IN
                  </button>
                </div>
              </div>
            </div>

            {/* Prize Zones */}
            <div className="tournament-control-panel">
              <label className="control-panel-label">🎯 PRIZE ZONES</label>
              <div className="prize-zones-grid">
                {TOURNAMENT_ZONES.slice(1).map((zone) => (
                  <div key={zone.id} className="prize-zone-card" style={{ borderColor: zone.color }}>
                    <div className="zone-icon" style={{ color: zone.color }}>{zone.icon}</div>
                    <div className="zone-details">
                      <div className="zone-name">{zone.name}</div>
                      <div className="zone-prize">{zone.payout}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Button */}
            <div className="tournament-control-panel">
              {needsApproval() ? (
                <button
                  className="tournament-action-btn tournament-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('tavernHum', 0.2)}
                  disabled={tournamentState.isApproving}
                >
                  {tournamentState.isApproving ? '⚔️ JOINING...' : '🏰 JOIN TOURNAMENT'}
                </button>
              ) : (
                <button
                  className="tournament-action-btn tournament-shoot-btn"
                  onClick={handleTakeShot}
                  onMouseEnter={() => playGameSound('tavernHum', 0.2)}
                  disabled={!canTakeShot()}
                >
                  {tournamentState.isAiming ? '🏹 AIMING...' : '🏹 TAKE SHOT'}
                </button>
              )}
            </div>

            {/* Potential Prize */}
            <div className="potential-prize-display">
              🎯 POTENTIAL WINNINGS: 1.5x - 20x your wager
            </div>
          </div>
        </div>

        {/* Tournament Leaderboard */}
        <div className="tournament-leaderboard">
          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🏹 YOUR STANDING</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Rank</span>
              <span className="entry-value">{tournamentState.archerRank}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Points</span>
              <span className="entry-value">{formatNumber(tournamentState.tournamentPoints)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Title</span>
              <span className="entry-value">{getArcherTitle(tournamentState.archerRank)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">🏰 TOURNAMENT HALL</h3>
            <div className="leaderboard-entry">
              <span className="entry-label">Active</span>
              <span className="entry-value">{tournamentStats.activeArchers}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Rounds</span>
              <span className="entry-value">{formatNumber(tournamentStats.totalRounds)}</span>
            </div>
            <div className="leaderboard-entry">
              <span className="entry-label">Prize Pool</span>
              <span className="entry-value">{formatNumber(tournamentStats.totalWagers)}</span>
            </div>
          </div>

          <div className="leaderboard-panel">
            <h3 className="leaderboard-title">📜 TOURNAMENT RULES</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Place your wager',
                'Aim at the target',
                'Take your shot',
                'Hit bullseye for 20x!'
              ].map((rule, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #8B4513, #DAA520)', 
                    color: '#FFF', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(139, 69, 19, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(218, 165, 32, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {rule}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {shotHistory.length > 0 && (
            <div className="leaderboard-panel">
              <h3 className="leaderboard-title">📜 RECENT SHOTS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {shotHistory.map((shot, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(139, 69, 19, 0.1)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${shot.won ? '#DAA520' : '#DC143C'}`,
                    border: '1px solid rgba(139, 69, 19, 0.3)'
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {TOURNAMENT_ZONES[shot.result]?.icon || '💨'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'rgba(218, 165, 32, 0.8)', fontSize: '0.8rem', fontWeight: '600' }}>
                          {TOURNAMENT_ZONES[shot.result]?.name || 'MISS'}
                        </div>
                        <div style={{ 
                          color: shot.won ? '#DAA520' : '#DC143C', 
                          fontWeight: '800', 
                          fontSize: '0.9rem'
                        }}>
                          {shot.won ? `+${formatNumber(shot.winnings)}` : 'MISS'}
                        </div>
                      </div>
                      <div style={{ color: 'rgba(139, 69, 19, 0.6)', fontSize: '0.7rem' }}>
                        Wager: {formatNumber(shot.wagerAmount)} BLB
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BullsEye;